import { useCallback } from "react"

import { useQueryClient } from "@tanstack/react-query"

import { type SheriffArea } from "@/constants/sheriffOptions"
import { supabase } from "@/lib/supabase"

// An open timesheet is one the person is still clocked into (not yet submitted).
const OPEN_TIMESHEET_STATUS = "InProgress" as const
// A claimed shift is one that has been filled by the person.
const CLAIMED_SHIFT_STATUS = "Filled" as const

export interface OpenTimesheet {
  id: string
  clocked_in_time: string | null
  shift_id: string | null
  shift: {
    start_time: string
    end_time: string
    area: SheriffArea | null
    location: string | null
  } | null
}

export interface UpcomingShift {
  id: string
  start_time: string
  end_time: string
  area: SheriffArea | null
  location: string | null
}

export interface ActiveEngagements {
  openTimesheets: OpenTimesheet[]
  upcomingShifts: UpcomingShift[]
  hasActiveEngagements: boolean
}

const fetchActiveEngagements = async (personId: string): Promise<ActiveEngagements> => {
  const [timesheetsResult, shiftsResult] = await Promise.all([
    supabase
      .from("timesheets")
      .select(
        "id, clocked_in_time, shift_id, shift:shifts!timesheets_shift_id_fkey(start_time, end_time, area, location)",
      )
      .eq("person_id", personId)
      .eq("status", OPEN_TIMESHEET_STATUS),
    supabase
      .from("shifts")
      .select("id, start_time, end_time, area, location")
      .eq("person_id", personId)
      .eq("status", CLAIMED_SHIFT_STATUS)
      .gte("start_time", new Date().toISOString())
      .order("start_time", { ascending: true }),
  ])

  if (timesheetsResult.error) throw timesheetsResult.error
  if (shiftsResult.error) throw shiftsResult.error

  // Supabase types a to-one embed as either an object or a single-element array;
  // normalise to a single shift (or null) for each timesheet.
  const openTimesheets: OpenTimesheet[] = (timesheetsResult.data ?? []).map((row) => {
    const shift = Array.isArray(row.shift) ? (row.shift[0] ?? null) : (row.shift ?? null)
    return {
      id: row.id,
      clocked_in_time: row.clocked_in_time,
      shift_id: row.shift_id,
      shift,
    }
  })
  const upcomingShifts = (shiftsResult.data ?? []) as UpcomingShift[]

  return {
    openTimesheets,
    upcomingShifts,
    hasActiveEngagements: openTimesheets.length > 0 || upcomingShifts.length > 0,
  }
}

/**
 * Returns a function that fetches a person's active engagements — the open (InProgress)
 * timesheets and upcoming (Filled) shifts that should be surfaced before rejecting their
 * application.
 */
export const useFetchActiveEngagements = () => {
  const queryClient = useQueryClient()

  return useCallback(
    async (personId?: string): Promise<ActiveEngagements> => {
      if (!personId)
        return {
          openTimesheets: [],
          upcomingShifts: [],
          hasActiveEngagements: false,
        }

      return queryClient.fetchQuery({
        queryKey: ["activeEngagements", personId],
        queryFn: () => fetchActiveEngagements(personId),
        staleTime: 30_000,
      })
    },
    [queryClient],
  )
}
